// The realtime connection (GET /v1/realtime, docs/web-konzept.md section 5.2, decision 10).
// One tab of the browser holds the WebSocket: the tabs elect it with a Web Lock, the others get
// its frames over a BroadcastChannel and take over when it closes. Five open tabs are one
// connection and one "online". The leader tells the server whether any tab is visible
// ({"t":"focus","visible":…}), so the server pushes to this browser while none is.

/** A frame from the server, e.g. {"t":"sync","seq":12} or {"t":"presence",…}. */
export interface ServerFrame {
  t: string;
  [field: string]: unknown;
}

type Listener = (frame: ServerFrame) => void;

type Message =
  | { kind: "frame"; frame: ServerFrame }
  | { kind: "visibility"; tab: string; visible: boolean }
  | { kind: "send"; frame: Record<string, unknown> }
  | { kind: "state"; connected: boolean };

const PING_MS = 25_000;
const MAX_BACKOFF_MS = 30_000;

export interface RealtimeOptions {
  /** A valid access token (Session.token). */
  token: () => Promise<string>;
  url?: string;
  /** Test seams. */
  webSocket?: typeof WebSocket;
  channel?: BroadcastChannel;
  locks?: LockManager;
  document?: VisibilitySource;
}

/** What Realtime needs of the document: whether this tab is visible, and when that changes. */
export interface VisibilitySource {
  readonly visibilityState: string;
  addEventListener(type: "visibilitychange", listener: () => void): void;
  removeEventListener(type: "visibilitychange", listener: () => void): void;
}

export class Realtime {
  private readonly listeners = new Set<Listener>();
  private readonly stateListeners = new Set<(connected: boolean) => void>();
  private readonly tab = crypto.randomUUID();
  private readonly visibleTabs = new Set<string>();
  private readonly channel: BroadcastChannel;
  private readonly options: RealtimeOptions;
  private socket: WebSocket | null = null;
  private leader = false;
  private stopped = false;
  private connected = false;
  private releaseLock: (() => void) | null = null;

  constructor(options: RealtimeOptions) {
    this.options = options;
    this.channel = options.channel ?? new BroadcastChannel("voxie-realtime");
  }

  get isConnected(): boolean {
    return this.connected;
  }

  /** Every frame from the server, in this tab, whichever tab holds the socket. */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onState(listener: (connected: boolean) => void): () => void {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  /** Sends a frame (e.g. {"t":"activity",…}) through the tab that holds the socket. */
  send(frame: Record<string, unknown>): void {
    if (this.leader) this.sendRaw(frame);
    else this.post({ kind: "send", frame });
  }

  start(): void {
    this.channel.onmessage = (event: MessageEvent<Message>) => this.onMessage(event.data);
    const doc = this.options.document ?? document;
    doc.addEventListener("visibilitychange", this.onVisibility);
    this.onVisibility();
    const locks = this.options.locks ?? navigator.locks;
    void locks.request("voxie-realtime", () => {
      if (this.stopped) return Promise.resolve();
      this.leader = true;
      // Tabs announced before this one led: ask again by announcing ourselves.
      this.post({ kind: "visibility", tab: this.tab, visible: doc.visibilityState === "visible" });
      void this.connect(1000);
      return new Promise<void>((release) => (this.releaseLock = release));
    });
  }

  stop(): void {
    this.stopped = true;
    const doc = this.options.document ?? document;
    doc.removeEventListener("visibilitychange", this.onVisibility);
    this.post({ kind: "visibility", tab: this.tab, visible: false });
    this.socket?.close(1000);
    this.socket = null;
    this.releaseLock?.();
    this.channel.close();
  }

  private readonly onVisibility = () => {
    const visible = (this.options.document ?? document).visibilityState === "visible";
    this.setVisible(this.tab, visible);
    this.post({ kind: "visibility", tab: this.tab, visible });
  };

  private setVisible(tab: string, visible: boolean) {
    const before = this.visibleTabs.size > 0;
    if (visible) this.visibleTabs.add(tab);
    else this.visibleTabs.delete(tab);
    const after = this.visibleTabs.size > 0;
    if (this.leader && before !== after) this.sendRaw({ t: "focus", visible: after });
  }

  private onMessage(message: Message) {
    switch (message.kind) {
      case "frame":
        this.emit(message.frame);
        break;
      case "visibility":
        this.setVisible(message.tab, message.visible);
        break;
      case "send":
        if (this.leader) this.sendRaw(message.frame);
        break;
      case "state":
        this.setConnected(message.connected);
        break;
    }
  }

  private async connect(backoff: number): Promise<void> {
    while (!this.stopped) {
      try {
        await this.open();
        backoff = 1000;
      } catch {
        // Fall through to the backoff.
      }
      this.setConnected(false, true);
      if (this.stopped) return;
      await new Promise((r) => setTimeout(r, backoff * (0.75 + Math.random() / 2)));
      backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
    }
  }

  /** One connection, from auth to close. */
  private open(): Promise<void> {
    return new Promise((resolve, reject) => {
      const Ws = this.options.webSocket ?? WebSocket;
      const url = this.options.url ?? `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/v1/realtime`;
      const socket = new Ws(url);
      this.socket = socket;
      let pinger: ReturnType<typeof setInterval> | undefined;
      socket.onopen = async () => {
        try {
          // Never in the URL, which ends up in logs: the first frame authenticates.
          socket.send(JSON.stringify({ t: "auth", token: await this.options.token() }));
        } catch (e) {
          socket.close();
          reject(e);
        }
      };
      socket.onmessage = (event) => {
        const frame = parse(event.data);
        if (!frame) return;
        if (frame.t === "ready") {
          this.setConnected(true, true);
          this.sendRaw({ t: "focus", visible: this.visibleTabs.size > 0 });
          pinger = setInterval(() => this.sendRaw({ t: "ping" }), PING_MS);
        }
        if (frame.t === "pong") return;
        this.emit(frame);
        this.post({ kind: "frame", frame });
      };
      socket.onclose = (event) => {
        clearInterval(pinger);
        if (this.socket === socket) this.socket = null;
        // 4401: the token was not accepted; the next attempt asks the session for a fresh one.
        if (event.code === 4401) reject(new Error("unauthenticated"));
        else resolve();
      };
      socket.onerror = () => undefined;
    });
  }

  private sendRaw(frame: Record<string, unknown>) {
    if (this.socket?.readyState === 1) this.socket.send(JSON.stringify(frame));
  }

  private setConnected(connected: boolean, announce = false) {
    if (announce) this.post({ kind: "state", connected });
    if (this.connected === connected) return;
    this.connected = connected;
    for (const listener of this.stateListeners) listener(connected);
  }

  private emit(frame: ServerFrame) {
    for (const listener of this.listeners) listener(frame);
  }

  private post(message: Message) {
    try {
      this.channel.postMessage(message);
    } catch {
      // Closed while stopping.
    }
  }
}

function parse(data: unknown): ServerFrame | null {
  if (typeof data !== "string") return null;
  try {
    const frame = JSON.parse(data) as ServerFrame;
    return typeof frame?.t === "string" ? frame : null;
  } catch {
    return null;
  }
}
